<template>
	<div class="container">
		<div class="face">
			<Mouth ref="mouth" class="face__mouth" />
			<Eyes ref="eyes" class="face__eyes" @mousedown.native="onClick" />
			<Speech ref="speech" class="face__speech" @speaking="onSpeaking" />
		</div>
	</div>
</template>

<script>
import Eyes from './Eyes'
import Mouth from './Mouth'
import Speech from './Speech'

import { enums } from 'protoswipe-shared'
import wait from '@/mixins/wait'
import campaign from '@/mixins/campaign'
import Tween from '@/plugins/Animator/lib/Tween'
import { choose, randInt } from '@/lib/helpers'
import { pointDist, toCartesian } from '@/lib/trig'
import { easeInOutSine, easeOutSine, easeOutElastic } from '@/lib/easings'

const vec2 = (x = 0, y = 0) => [x, y]

const BASE_RADIUS = 80
const HEADER_H = 80
const SPEECH_H = 64
const LONG_DIST_THRESHOLD = 300
const POKE_DISP = 65
const MOUTH_DELAY = 100
const INTRO_DELAY = 750
const SPEECH_HISTORY = 3

const { Dialogue, GlobalDialogue } = enums

export default {
	name: 'Avatar',
	props: {
		radius: {
			type: [Number, Array],
			default: BASE_RADIUS
		}
	},
	data() {
		const start = vec2(0, -1000)
		return {
			eyes: [...start],
			mouth: [...start],
			speech: [...start],
			speechHistory: [],
			bounds: {
				top: 0,
				left: 0,
				width: 0,
				height: 0
			},
			tween: {
				eyes: null,
				mouth: null,
				speech: null
			},
			timeout: {
				intro: null,
				move: null
			},
			movePromise: null,
			pokes: 0,
			pokedAt: null
		}
	},
	computed: {
		rx() {
			return Array.isArray(this.radius) ? this.radius[0] : this.radius
		},
		ry() {
			return Array.isArray(this.radius) ? this.radius[1] : this.radius
		},
		center() {
			return vec2(0, -SPEECH_H / 2 - this.ry / 4)
		},
		range() {
			const wHalf = this.bounds.width / 2
			const hHalf = this.bounds.height / 2
			const x = [this.rx - wHalf, wHalf - this.rx]
			const y = [this.ry - hHalf, hHalf - this.ry]
			return { x, y }
		}
	},
	methods: {
		measure() {
			this.bounds = this.$el.getBoundingClientRect()
		},
		ref(name, callback) {
			const ref = this.$refs[name]
			if (ref) callback(ref)
		},
		lookAt(vec, opts) {
			// Transform relative to world coords
			const { top, left, width, height } = this.bounds
			const x = left + width / 2 + vec[0]
			const y = top + height / 2 + vec[1]
			this.ref('eyes', ref => ref.lookAt([x, y], opts))
		},
		lookAtWorld(vec, opts) {
			this.ref('eyes', ref => ref.lookAt(vec, opts))
		},
		moveToCenter(opts) {
			return this.moveTo(this.center, opts)
		},
		moveToRandom(opts) {
			const { x, y } = this.range
			const target = [x, y].map(range => randInt(...range))
			return this.moveTo(target, opts)
		},
		moveTo(
			to,
			{ ease = easeInOutSine, duration, moveSpeech = true, look = false } = {}
		) {
			const type = Tween.TYPE_VEC2
			const opts = { to, ease, type }

			// Cancel existing movement tweens
			this.removeTween(this.tween.eyes)
			this.removeTween(this.tween.mouth)
			this.removeTween(this.tween.speech)

			// Calculate speed
			const dist = pointDist(this.eyes, to)
			const time = duration || (dist > LONG_DIST_THRESHOLD ? 800 : 500)

			// Look in direction if look flag is true
			if (look) this.lookAt(to)

			// Create new tweens
			if (moveSpeech) {
				this.tween.speech = this.addTween(
					{ from: this.speech, duration: time, ...opts },
					value => (this.speech = value)
				)
			}
			this.tween.mouth = this.addTween(
				{ from: this.mouth, duration: time + MOUTH_DELAY, ...opts },
				value => (this.mouth = value)
			)
			this.movePromise = new Promise(resolve => {
				this.tween.eyes = this.addTween(
					{ from: this.eyes, duration: time, ...opts },
					value => (this.eyes = value),
					() => {
						// Reset eyes if needed and resolve promise
						if (look) this.resetEyes()
						resolve()
					}
				)
			})
			return this.movePromise
		},
		emote(eyes, mouth) {
			this.ref('eyes', ref => ref.set(eyes))
			this.ref('mouth', ref => ref.set(mouth))
		},
		getDialogue(type) {
			if (type in GlobalDialogue) {
				return choose(this.$store.state.config.dialogue[type] || [])
			} else if (this.campaignLoaded && type in Dialogue) {
				return choose(this.campaign.dialogue[type] || [])
			}
			return []
		},
		updateSpeechHistory(speechType) {
			// Add to history, but only ever add "poked" once
			const poked = GlobalDialogue.poked
			if (speechType === poked && this.speechHistory[0] === poked) return

			// Emulate FILO
			this.speechHistory.unshift(speechType)
			if (this.speechHistory.length > SPEECH_HISTORY) this.speechHistory.pop()
		},
		speechAdd(speechType, override) {
			this.ref('speech', ref => {
				this.updateSpeechHistory(speechType)
				const text = this.getDialogue(speechType)
				ref.add(text, override)
			})
		},
		speechStart({ disappear = false } = {}) {
			this.ref('speech', ref => {
				ref.setDisappear(disappear)
				ref.start()
			})
		},
		speechStop() {
			this.ref('speech', ref => ref.stop())
		},
		resetEyes() {
			this.ref('eyes', ref => ref.reset())
		},
		resetMouth() {
			this.ref('mouth', ref => ref.reset())
		},
		reset() {
			this.resetEyes()
			this.resetMouth()
		},
		// Emotes
		eShock() {
			this.emote(true, 'shock')
		},
		eUnimpressed() {
			this.emote(false, 'closed')
		},
		eExcited() {
			this.emote(false, 'wide')
		},
		eSmiling() {
			this.emote(true, 'smile')
		},
		// Actions
		async aPoke() {
			// Set pokedAt position
			if (!this.pokes) this.pokedAt = [...this.eyes]

			// Start poke anim
			this.pokes++
			this.speechStop()
			this.eUnimpressed()

			const angle = Math.random() * 2 * Math.PI
			const offset = toCartesian([POKE_DISP, angle])
			const tx = this.pokedAt[0] + offset[0]
			const ty = this.pokedAt[1] + offset[1]

			// Shoot off in a random direction
			await this.moveTo([tx, ty], {
				ease: easeOutSine,
				duration: 100,
				moveSpeech: false
			})

			// Speak poked text, queue previous text if it exists
			this.speechAdd(GlobalDialogue.poked, true)
			const prev = this.speechHistory[1]
			if (prev) this.speechAdd(prev)

			// Start speaking
			this.speechStart()

			// Spring back to previous position!
			await this.moveTo(this.pokedAt, {
				ease: easeOutElastic,
				duration: 500,
				moveSpeech: false
			})

			this.resetEyes()
			this.pokes--
		},
		async aLookDown() {
			const [cx, cy] = this.center
			const ty = cy + 30

			this.eShock()
			this.lookAt([0, 200])
			await this.moveTo([cx, ty], { ease: easeOutSine, duration: 500 })
			await this.moveTo(this.center, { ease: easeOutElastic, duration: 500 })

			// Look up after a time, but don't count as part of this animation
			this.wait(1500).then(this.resetEyes)
		},
		aLeave() {
			const [cx, cy] = this.center
			const [x, y] = this.eyes

			this.eShock()
			this.lookAt([x, y - 200])
			const ty = cy - this.bounds.height
			return this.moveTo([cx, ty], {
				ease: easeInOutSine,
				duration: 500,
				moveSpeech: false
			})
		},
		// Event listeners & cleanup
		onSpeaking(speaking) {
			this.ref('mouth', ref => {
				if (speaking) ref.talk()
				else ref.reset()
			})
		},
		onClick() {
			this.aPoke()
		},
		clearTimeouts() {
			Object.entries(this.timeout).forEach(window.clearTimeout)
		}
	},
	tick() {
		// Update element positions
		this.ref('eyes', ref => ref.moveTo(this.eyes))
		this.ref('mouth', ref => ref.moveTo(this.mouth))
		this.ref('speech', ref => ref.moveTo(this.speech))
	},
	mounted() {
		// Measure and move avatar just off top of screen
		this.measure()
		this.eyes[1] = -this.bounds.height - HEADER_H - this.ry
		this.mouth[1] = -this.bounds.height - HEADER_H - this.ry

		// Animate in
		this.timeout.intro = window.setTimeout(() => {
			this.lookAt(this.center)
			this.$refs.mouth.set('shock')
			this.moveTo(this.center, { ease: easeOutElastic }).then(this.reset)
		}, INTRO_DELAY)
		window.addEventListener('resize', this.measure)
	},
	beforeDestroy() {
		this.clearTimeouts()
		window.removeEventListener('resize', this.measure)
	},
	mixins: [campaign, wait],
	components: {
		Eyes,
		Mouth,
		Speech
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/mq';

.container {
	position: relative;

	@include mq(md) {
		height: 22rem;
	}
}

.face {
	position: absolute;
	width: 0;
	height: 0;
	top: 50%;
	left: 50%;

	&__eyes {
		top: -2.5em;
		left: 0;

		&::before {
			content: '';
			position: absolute;
			top: calc(2.5em - 75px);
			left: -75px;
			width: 150px;
			height: 150px;
			// DEBUG
			// background-color: rgba(white, 0.25);
		}
	}

	&__mouth {
		top: 2.5em;
		left: 0;
	}

	&__speech {
		top: 8.25em;
		left: 0;
	}
}
</style>
